import React, {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {AppTitle, AppText, AppSmallText, AppErrorText} from '../ui/Typography';
import {useNotificationStore} from '../../../features/notifications/store/notificationStore';
import {useError} from '../../contexts/ErrorContext';
import {ErrorType} from '../../../Data/config/AppError';
import {Notification} from '../../../Services/common/NotificationTypes';

const ICONS = {
  success: (
    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-100 border-2 border-green-400">
      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
    </span>
  ),
  error: (
    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-100 border-2 border-red-400">
      <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </span>
  ),
  warning: (
    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-yellow-100 border-2 border-yellow-400">
      <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12A9 9 0 113 12a9 9 0 0118 0z" />
      </svg>
    </span>
  ),
  info: (
    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-brand-100 border-2 border-brand-400">
      <svg className="w-5 h-5 text-brand-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M12 20a8 8 0 100-16 8 8 0 000 16z" />
      </svg>
    </span>
  )
};

const getNotificationStyle = (type: Notification['type'] | 'critical-error') => {
  switch (type) {
    case 'success':
      return 'bg-green-50 border-green-400 text-green-800';
    case 'error':
      return 'bg-red-50 border-red-400 text-red-800';
    case 'critical-error':
      return 'bg-red-50 border-r-4 border-red-500 text-red-800';
    case 'warning':
      return 'bg-yellow-50 border-yellow-400 text-yellow-800';
    case 'info':
      return 'bg-brand-50 border-brand-400 text-brand-800';
    default:
      return 'bg-gray-50 border-gray-400 text-gray-800';
  }
};

const getErrorTitle = (type: ErrorType): string => {
  switch (type) {
    case ErrorType.NETWORK:
      return 'خطأ في الاتصال';
    case ErrorType.AUTHENTICATION:
      return 'خطأ في المصادقة';
    case ErrorType.AUTHORIZATION:
      return 'خطأ في الصلاحيات';
    case ErrorType.VALIDATION:
      return 'خطأ في التحقق';
    case ErrorType.NOT_FOUND:
      return 'لم يتم العثور على البيانات';
    case ErrorType.SERVER:
      return 'خطأ في الخادم';
    default:
      return 'خطأ غير متوقع';
  }
};


interface UnifiedNotification {
  id: string;
  type: Notification['type'] | 'critical-error';
  message: string;
  title?: string;
  details?: string;
  isCritical?: boolean;
}

/**
 * Unified Notification Container
 *
 * This component combines both regular notifications and critical errors
 * into a single, unified display system. It handles:
 * - Success, warning, info notifications
 * - Regular errors
 * - Critical authentication/authorization errors from ErrorContext
 *
 * Critical errors are displayed first, followed by regular notifications.
 * All notifications are stacked vertically with proper spacing.
 */
const UnifiedNotificationContainer: React.FC = () => {
  const {t} = useTranslation();
  const {notifications, removeNotification} = useNotificationStore();
  const {error: criticalError, clearError} = useError();

  // Convert critical error to notification format for unified display
  const criticalErrorNotification: UnifiedNotification | null = criticalError
    ? {
        id: 'critical-error',
        type: 'critical-error',
        message: criticalError.message,
        title: getErrorTitle(criticalError.type),
        details: criticalError.details
          ? typeof criticalError.details === 'string'
            ? criticalError.details
            : JSON.stringify(criticalError.details)
          : undefined,
        isCritical: true
      }
    : null;

  // Auto-dismiss critical errors after 5 seconds (except authentication errors)
  useEffect(() => {
    if (criticalError && criticalError.type !== ErrorType.AUTHENTICATION) {
      const timer = setTimeout(() => {
        clearError();
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [criticalError, clearError]);

  // Combine all notifications - critical errors appear first for priority
  const allNotifications: UnifiedNotification[] = [
    ...(criticalErrorNotification ? [criticalErrorNotification] : []),
    ...notifications.map(n => ({
      id: n.id,
      type: n.type,
      message: n.message,
      title: n.title,
      details: n.details,
      isCritical: false
    }))
  ];

  if (allNotifications.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-[2000] space-y-3" style={{pointerEvents: 'auto'}}>
      {allNotifications.map((notification) => (
        <div
          key={notification.id}
          className={`flex flex-row-reverse items-center border rounded-2xl shadow-lg px-4 py-3 min-w-[340px] max-w-md animate-slide-in ${getNotificationStyle(
            notification.type
          )}`}
          role="alert"
          style={{pointerEvents: 'auto'}}
        >
          {ICONS[notification.type === 'critical-error' ? 'error' : notification.type]}
          <div className="mr-6 flex-1 text-right">
            <AppTitle className="text-base mb-1 font-bold">
              {notification.title ||
                (notification.type === 'success'
                  ? t('تمت العملية بنجاح')
                  : notification.type === 'error' || notification.type === 'critical-error'
                    ? t('حدث خطأ')
                    : '')}
            </AppTitle>
            {notification.type === 'critical-error' ? (
              <AppErrorText className="text-sm">{notification.message}</AppErrorText>
            ) : (
              <AppText className="text-sm">{notification.message}</AppText>
            )}
            {notification.details && (
              <AppSmallText className="mt-1 text-xs opacity-80">{notification.details}</AppSmallText>
            )}
          </div>
          <button
            className="mr-4 text-gray-400 hover:text-gray-600 focus:outline-none text-2xl font-bold"
            onClick={() => {
              if (notification.id === 'critical-error') {
                clearError();
              } else {
                removeNotification(notification.id);
              }
            }}
            style={{pointerEvents: 'auto'}}
            aria-label={t('close')}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
};

export default UnifiedNotificationContainer;

